import { DetailRow, InputRow, SelectRow } from "../Rows";
import { FormCard, FormGrid } from "../FormCard";
import { useTranslation } from "react-i18next";
import { RoleLabelsForExternal, Roles } from "../../providers/AuthContext";
import {
    getExternalUserRoleOptions,
    formatTermsAccepted,
    shouldExternalUserBelongToOrganization,
} from "./externalUserUtils";

const ExternalUserForm = ({
    user,
    invalidFields = [],
    organizationOptions = [],
    orgLoading = false,
    onChange,
    editableFields = {},
    showCreated = false,
    title,
}) => {
    const { t } = useTranslation();
    const cardTitle = title === undefined ? t("form.userDetails") : title;
    const isEditable = (field) => Boolean(editableFields[field]);

    const shouldBelongToOrg = shouldExternalUserBelongToOrganization(
        user.externalUserRole
    );

    const selectedOrganization = organizationOptions.find(
        (org) => org.value === user.organizationPublicId
    );

    return (
        <FormCard title={cardTitle}>
            <FormGrid>
                {/* First name */}
                {isEditable("firstName") ? (
                    <InputRow
                        label={t("common.firstName")}
                        value={user.firstName || ""}
                        onChange={(value) => onChange("firstName", value)}
                        invalid={invalidFields.includes("firstName")}
                        maxLength={60}
                    />
                ) : (
                    <DetailRow label={t("common.firstName")} value={user.firstName} />
                )}

                {/* Last name */}
                {isEditable("lastName") ? (
                    <InputRow
                        label={t("common.lastName")}
                        value={user.lastName || ""}
                        onChange={(value) => onChange("lastName", value)}
                        invalid={invalidFields.includes("lastName")}
                        maxLength={60}
                    />
                ) : (
                    <DetailRow label={t("common.lastName")} value={user.lastName} />
                )}

                {/* Email */}
                {isEditable("email") ? (
                    <InputRow
                        label={t("common.email")}
                        value={user.email || ""}
                        onChange={(value) => onChange("email", value)}
                        invalid={invalidFields.includes("email")}
                        maxLength={120}
                    />
                ) : (
                    <DetailRow label={t("common.email")} value={user.email} />
                )}

                {/* Role */}
                {isEditable("externalUserRole") ? (
                    <SelectRow
                        label={t("common.role")}
                        value={user.externalUserRole || ""}
                        options={getExternalUserRoleOptions(t)}
                        isMulti={false}
                        invalid={invalidFields.includes("externalUserRole")}
                        onChange={(value) => {
                            onChange("externalUserRole", value);

                            if (value === Roles.Customer) {
                                onChange("organizationPublicId", "");
                            }
                        }}
                    />
                ) : (
                    <DetailRow
                        label={t("common.role")}
                        value={
                            t(`role.${user.externalUserRole}`, {
                                defaultValue:
                                    RoleLabelsForExternal[user.externalUserRole] ??
                                    user.externalUserRole ??
                                    "",
                            })
                        }
                    />
                )}

                {/* Created */}
                {showCreated && (
                    <DetailRow
                        label={t("common.createdAt")}
                        value={user.createdDateTimeUtc}
                    />
                )}

                {showCreated && (
                    <DetailRow
                        label={t("table.termsAccepted")}
                        value={formatTermsAccepted(user.termsAccepted, t)}
                    />
                )}

                {/* Organisation */}
                {shouldBelongToOrg &&
                    (isEditable("organizationPublicId") ? (
                        <SelectRow
                            label={t("entity.organization")}
                            value={user.organizationPublicId || ""}
                            options={organizationOptions}
                            isMulti={false}
                            disabled={orgLoading}
                            invalid={invalidFields.includes(
                                "organizationPublicId"
                            )}
                            onChange={(value) =>
                                onChange("organizationPublicId", value)
                            }
                        />
                    ) : (
                        <DetailRow
                            label={t("entity.organization")}
                            value={
                                selectedOrganization?.label ||
                                user.organizationPublicId
                            }
                        />
                    ))}
            </FormGrid>
        </FormCard>
    );
};

export default ExternalUserForm;